import type { DriftReport } from "./types.js";

export interface GateOptions {
  /** Fail if the drift score is below this value (0–100). */
  minScore?: number;
  /** Fail if the number of warnings exceeds this value. */
  maxWarnings?: number;
  /** Fail if any error-severity issue is present. Defaults to true. */
  failOnError?: boolean;
}

export interface GateResult {
  passed: boolean;
  /** Human-readable reasons the gate failed (empty when passed). */
  reasons: string[];
}

/**
 * Evaluate a drift report against CI gate thresholds. Pure — no process.exit,
 * no I/O — so the gate policy is testable in isolation and the CLI only has to
 * translate the result into an exit code.
 *
 * Default policy (no options) fails on any error and passes otherwise.
 * Thresholds tighten the gate; they never loosen it.
 */
export function evaluateGate(report: DriftReport, opts: GateOptions = {}): GateResult {
  const failOnError = opts.failOnError ?? true;
  const reasons: string[] = [];

  if (failOnError) {
    const errors = report.issues.filter((i) => i.severity === "error").length;
    if (errors > 0) {
      reasons.push(`${errors} error${errors === 1 ? "" : "s"} present`);
    }
  }

  if (opts.minScore !== undefined && report.score < opts.minScore) {
    reasons.push(`drift score ${report.score} is below the minimum ${opts.minScore}`);
  }

  if (opts.maxWarnings !== undefined) {
    const warnings = report.issues.filter((i) => i.severity === "warning").length;
    if (warnings > opts.maxWarnings) {
      reasons.push(`${warnings} warnings exceed the maximum ${opts.maxWarnings}`);
    }
  }

  return { passed: reasons.length === 0, reasons };
}
